// Cliente centralizado de API para todos los servicios backend del KYC.
// Todos los fetch calls viven aquí — los componentes nunca hacen fetch directamente.

// En sandbox cada var apunta directamente al Cloud Run del servicio.
// En preprod/prod cada var incluye el prefijo de path del Load Balancer
// (ej. DOC_HANDLER_URL=https://kyc-lb.mx/doc-handler) para preservar el routing existente.
const INTEGRATION_API_URL = process.env.REACT_APP_INTEGRATION_API_URL ?? '';
const DOC_HANDLER_URL = process.env.REACT_APP_DOC_HANDLER_URL ?? '';
const AI_SERVICE_URL = process.env.REACT_APP_AI_SERVICE_URL ?? '';
const WORKFLOWS_URL = process.env.REACT_APP_WORKFLOWS_URL ?? '';

// URL del liveness-service — servicio separado con autenticación X-API-Key.
// Seguridad: la API key se inyecta en tiempo de build desde variables de entorno.
// Para producción considerar proxiar a través del core-api en lugar de llamada directa.
const LIVENESS_BASE_URL = process.env.REACT_APP_LIVENESS_BASE_URL ?? '';
const LIVENESS_API_KEY = process.env.REACT_APP_LIVENESS_API_KEY ?? '';

// URL del core-api — usada para consultar estadísticas agregadas del KYC
const CORE_API_URL = process.env.REACT_APP_CORE_API_URL ?? '';

// ─────────────────────────────────────────────────────────────────────────────
// Tipos de request y response
// ─────────────────────────────────────────────────────────────────────────────

// Request para subir un documento al doc-handler
interface UploadDocumentRequest {
  file: File;
  sessionId: string;
  token: string;
}

// Response del upload — campos retornados por el doc-handler
// process_id es el UUID que identifica el documento en GCS y en los pasos siguientes del pipeline.
interface UploadDocumentResponse {
  processId: string;
  filename: string;
  fileUri: string;
  fileUrl: string;
}

// Request para obtener el score de autenticidad de un documento
interface ScoreDocumentRequest {
  fileId: string;
  sessionId: string;
  token: string;
  docType: string; // 'ine' | 'passport' | 'proof_of_address'
}

// Response del scoring desde el ai-service — score 0-100 con razón de rechazo opcional
interface ScoreDocumentResponse {
  score: number;
  passed: boolean;
  requiresPOA: boolean; // true si INE sin dirección o Pasaporte
  rejectionReason?: string;
  details: Record<string, unknown>;
}

// Request para ejecutar un paso del pipeline en el workflows-handler
interface ExecuteWorkflowRequest {
  action: 'classification' | 'extraction' | 'validation' | 'storage' | 'pipeline';
  sessionId: string;
  token: string;
  payload: Record<string, unknown>;
}

// Response del workflows-handler con ID de ejecución para seguimiento
interface ExecuteWorkflowResponse {
  executionId: string;
  status: string;
}

// Resumen por usuario para el drill-down del dashboard. Sin PII.
interface UserSummary {
  user_id: string;
  status_bucket: string;                    // APROBADO / RECHAZADO / PENDIENTE
  attempts_count: number;
  last_attempt_status: string | null;
  last_updated: string | null;
}

// Response de GET /api/v1/stats/summary del core-api
interface StatsResponse {
  total_processes: number;
  total_claims: number;
  odp_deliveries: number;
  claims_by_status: Record<string, number>;
  claims_by_type: Record<string, number>;
  last_updated: string | null;
  users: UserSummary[];
}

// Error tipado que devuelve la capa de servicio — nunca expone objetos crudos de fetch
interface ApiError {
  status: number;
  message: string;
  details?: unknown;
}

// ─────────────────────────────────────────────────────────────────────────────
// Clase de error tipada
// ─────────────────────────────────────────────────────────────────────────────

// Error lanzado por todas las funciones de kycApi — permite catch tipado en componentes
export class KYCApiError extends Error {
  readonly status: number;
  readonly details: unknown;

  constructor(error: ApiError) {
    super(error.message);
    this.name = 'KYCApiError';
    this.status = error.status;
    this.details = error.details;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers internos
// ─────────────────────────────────────────────────────────────────────────────

// Construye los headers de autenticación con Bearer token (servicios KYC estándar)
function buildAuthHeaders(token: string): HeadersInit {
  return {
    Authorization: `Bearer ${token}`,
  };
}

// Construye los headers de autenticación con X-API-Key (liveness-service)
function buildLivenessHeaders(): HeadersInit {
  return {
    'X-API-Key': LIVENESS_API_KEY,
    'Content-Type': 'application/json',
  };
}

// Parsea la respuesta de error HTTP en un ApiError tipado
async function parseErrorResponse(response: Response): Promise<ApiError> {
  let details: unknown;

  try {
    details = await response.json();
  } catch {
    // Si el cuerpo no es JSON válido, dejamos details como undefined
    details = undefined;
  }

  // Intenta extraer mensaje legible del cuerpo si existe
  const message =
    typeof details === 'object' &&
    details !== null &&
    'detail' in details &&
    typeof (details as Record<string, unknown>).detail === 'string'
      ? (details as Record<string, string>).detail
      : `Error ${response.status}: ${response.statusText}`;

  return { status: response.status, message, details };
}

// ─────────────────────────────────────────────────────────────────────────────
// Funciones públicas de la capa de servicio
// ─────────────────────────────────────────────────────────────────────────────

// Sube un documento al doc-handler vía multipart/form-data.
// El doc-handler espera el campo "files" (plural) — retorna process_id y filename
// que se usan como parámetros de entrada en los GCP Workflows posteriores.
export async function uploadDocument(
  request: UploadDocumentRequest
): Promise<UploadDocumentResponse> {
  const formData = new FormData();
  // El endpoint del doc-handler espera el campo "files" (plural, List[UploadFile])
  formData.append('files', request.file);

  const response = await fetch(`${DOC_HANDLER_URL}/api/v1/storage/upload`, {
    method: 'POST',
    headers: buildAuthHeaders(request.token),
    body: formData,
  });

  if (!response.ok) {
    const error = await parseErrorResponse(response);
    throw new KYCApiError(error);
  }

  // El backend retorna snake_case — mapeamos a camelCase para el frontend
  const raw = await response.json() as {
    process_id: string;
    filename: string;
    file_uri: string;
    file_url: string;
  };

  return {
    processId: raw.process_id,
    filename: raw.filename,
    fileUri: raw.file_uri,
    fileUrl: raw.file_url,
  };
}

// Obtiene el score de autenticidad 0-100 del documento via ai-service
export async function scoreDocument(
  request: ScoreDocumentRequest
): Promise<ScoreDocumentResponse> {
  const response = await fetch(`${AI_SERVICE_URL}/api/v1/gemini/score-document`, {
    method: 'POST',
    headers: {
      ...buildAuthHeaders(request.token),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      file_id: request.fileId,
      session_id: request.sessionId,
      doc_type: request.docType,
    }),
  });

  if (!response.ok) {
    const error = await parseErrorResponse(response);
    throw new KYCApiError(error);
  }

  return response.json() as Promise<ScoreDocumentResponse>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Liveness Service — tipos de request y response
// ─────────────────────────────────────────────────────────────────────────────

// Response de POST /api/v1/liveness/sessions
interface CreateLivenessSessionResponse {
  session_id: string;
}

// Response de GET /api/v1/liveness/sessions/{id}/result
interface LivenessResultResponse {
  session_id: string;
  status: 'CREATED' | 'IN_PROGRESS' | 'SUCCEEDED' | 'FAILED' | 'EXPIRED';
  confidence: number | null;
  is_live: boolean | null;
  // null cuando la comparación facial no se ejecutó o falló técnicamente (no bloquea)
  face_match: boolean | null;
  face_match_score: number | null;
}

// Response de GET /api/v1/liveness/credentials
interface LivenessCredentialsResponse {
  access_key_id: string;
  secret_access_key: string;
  session_token?: string;
  expiration?: string; // ISO8601
}

// ─────────────────────────────────────────────────────────────────────────────
// Liveness Service — funciones públicas
// ─────────────────────────────────────────────────────────────────────────────

// Crea una nueva sesión de liveness en el liveness-service.
// Retorna el session_id que debe pasarse al FaceLivenessDetector del SDK de Amplify.
export async function createLivenessSession(): Promise<CreateLivenessSessionResponse> {
  const response = await fetch(`${LIVENESS_BASE_URL}/api/v1/liveness/sessions`, {
    method: 'POST',
    headers: buildLivenessHeaders(),
  });

  if (!response.ok) {
    const error = await parseErrorResponse(response);
    throw new KYCApiError(error);
  }

  return response.json() as Promise<CreateLivenessSessionResponse>;
}

// Consulta el resultado de una sesión de liveness.
// Llamar después de que el SDK de Amplify notifique via onAnalysisComplete.
// HTTP 202 indica que el video aún está procesándose — reintentar con polling.
// Cuando se provee docFilename, el liveness-service ejecuta comparación facial
// (Rekognition CompareFaces) y retorna face_match y face_match_score adicionales.
export async function getLivenessResult(
  livenessSessionId: string,
  userId?: string,
  docFilename?: string,
): Promise<LivenessResultResponse> {
  // Se usa concatenación de string en lugar de new URL(path, base) porque cuando el path
  // comienza con '/', new URL descarta el segmento de ruta del base (ej. /liveness),
  // enviando la petición al LB incorrecto. Mismo patrón que createLivenessSession.
  const params = new URLSearchParams();
  if (userId) params.set('user_id', userId);
  if (docFilename) params.set('doc_filename', docFilename);
  const query = params.toString() ? `?${params.toString()}` : '';
  const urlStr = `${LIVENESS_BASE_URL}/api/v1/liveness/sessions/${encodeURIComponent(livenessSessionId)}/result${query}`;

  const response = await fetch(urlStr, {
    method: 'GET',
    headers: buildLivenessHeaders(),
  });

  if (!response.ok && response.status !== 202) {
    const error = await parseErrorResponse(response);
    throw new KYCApiError(error);
  }

  return response.json() as Promise<LivenessResultResponse>;
}

// Obtiene credenciales AWS temporales del liveness-service para que el SDK de Amplify
// pueda conectarse directamente a Rekognition / KVS desde el navegador.
// Requiere que el endpoint GET /api/v1/liveness/credentials esté implementado en el backend.
export async function getLivenessCredentials(): Promise<LivenessCredentialsResponse> {
  const response = await fetch(`${LIVENESS_BASE_URL}/api/v1/liveness/credentials`, {
    method: 'GET',
    headers: buildLivenessHeaders(),
  });

  if (!response.ok) {
    const error = await parseErrorResponse(response);
    throw new KYCApiError(error);
  }

  return response.json() as Promise<LivenessCredentialsResponse>;
}

// Ejecuta un paso del pipeline KYC en el workflows-handler
export async function executeWorkflow(
  request: ExecuteWorkflowRequest
): Promise<ExecuteWorkflowResponse> {
  const response = await fetch(`${WORKFLOWS_URL}/api/v1/workflows/execute`, {
    method: 'POST',
    headers: {
      ...buildAuthHeaders(request.token),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      action: request.action,
      session_id: request.sessionId,
      payload: request.payload,
    }),
  });

  if (!response.ok) {
    const error = await parseErrorResponse(response);
    throw new KYCApiError(error);
  }

  return response.json() as Promise<ExecuteWorkflowResponse>;
}

// Consulta las estadísticas agregadas del proceso KYC desde el core-api.
// El endpoint es público (sin auth) — retorna solo conteos agregados, sin PII.
export async function getStats(): Promise<StatsResponse> {
  const response = await fetch(`${CORE_API_URL}/api/v1/stats/summary`, {
    method: 'GET',
  });

  if (!response.ok) {
    const error = await parseErrorResponse(response);
    throw new KYCApiError(error);
  }

  return response.json() as Promise<StatsResponse>;
}

// ─────────────────────────────────────────────────────────────────────────────
// D-6 — Revisión humana asíncrona
// ─────────────────────────────────────────────────────────────────────────────

// Marca la sesión KYC como PENDING_HUMAN_REVIEW en Firestore y publica en Pub/Sub
// para notificar al equipo validador interno.
// Llamar solo cuando al menos un documento acumuló requiresHumanReview=true durante el flujo.
export async function submitHumanReview(sessionId: string, token: string): Promise<void> {
  const response = await fetch(
    `${INTEGRATION_API_URL}/api/v1/kyc/human-review/${encodeURIComponent(sessionId)}`,
    {
      method: 'POST',
      headers: buildAuthHeaders(token),
    }
  );

  if (!response.ok) {
    const error = await parseErrorResponse(response);
    throw new KYCApiError(error);
  }
}

// Construye la URL del SSE stream — usada por useKYCStream para suscribirse a eventos.
// El stream vive en la integration-api (Firestore polling), no en el workflows-handler.
export function getStreamUrl(sessionId: string, token: string): string {
  return `${INTEGRATION_API_URL}/api/v1/workflows/stream/${sessionId}?token=${encodeURIComponent(token)}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// D-6.5 — Fusión de INE (anverso + reverso)
// ─────────────────────────────────────────────────────────────────────────────

// Request para fusionar los dos lados de una INE en un único PDF de 2 páginas
interface MergeIneRequest {
  // Archivo del reverso seleccionado por el usuario
  fileBack: File;
  // Nombre del anverso ya almacenado en GCS (retornado por el evento ine_back_required)
  filenameFront: string;
  // ID de la sesión KYC activa
  sessionId: string;
  // JWT de la sesión para autenticación
  token: string;
}

// Response del endpoint POST /api/v1/documents/merge-ine
interface MergeIneResponse {
  // Nombre del PDF fusionado en GCS — se pasa al workflow de clasificación
  filename: string;
  // UUID que identifica el archivo merged — se pasa al workflow de clasificación
  processId: string;
}

// Fusiona el anverso y el reverso de una INE en un único PDF de 2 páginas.
// Llama a POST {DOC_HANDLER_URL}/api/v1/documents/merge-ine con multipart/form-data.
// El PDF resultante se pasa al workflow de clasificación para continuar el pipeline.
export async function mergeIneFiles(request: MergeIneRequest): Promise<MergeIneResponse> {
  const formData = new FormData();
  formData.append('file', request.fileBack);
  formData.append('filename_front', request.filenameFront);
  formData.append('session_id', request.sessionId);

  const response = await fetch(`${DOC_HANDLER_URL}/api/v1/documents/merge-ine`, {
    method: 'POST',
    headers: buildAuthHeaders(request.token),
    body: formData,
  });

  if (!response.ok) {
    const error = await parseErrorResponse(response);
    throw new KYCApiError(error);
  }

  const raw = await response.json() as { filename: string; process_id: string };
  return {
    filename: raw.filename,
    processId: raw.process_id,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// D-7 — Estado KYC por usuario
// ─────────────────────────────────────────────────────────────────────────────

// Resultado del último proceso KYC terminado del usuario
interface KycLastResult {
  status: string;
  score: number | null;
  completedAt: string | null;
  rejectionReason: string | null;
}

// Response del endpoint GET /api/v1/kyc/user-status/{user_id}
interface UserKycStatusResponse {
  userId: string;
  status: 'none' | 'in_progress' | 'awaiting_liveness' | 'human_review' | 'completed' | 'rejected' | 'error';
  activeProcessId: string | null;
  lastResult: KycLastResult | null;
  canStartNew: boolean;
  canReset: boolean;
  // Paso del flujo pendiente cuando el proceso está in_progress esperando input del usuario.
  // "awaiting_poa" indica que el widget debe retomar en el paso de carga de comprobante.
  flowStep?: string | null;
}

// Consulta el estado KYC actual del usuario desde el workflows-handler.
// Retorna 'none' si el usuario nunca ha iniciado un proceso o no existe en kyc_users.
export async function getUserKycStatus(
  userId: string,
  token: string
): Promise<UserKycStatusResponse> {
  const response = await fetch(
    `${WORKFLOWS_URL}/api/v1/kyc/user-status/${encodeURIComponent(userId)}`,
    {
      method: 'GET',
      headers: buildAuthHeaders(token),
    }
  );

  if (!response.ok) {
    const error = await parseErrorResponse(response);
    throw new KYCApiError(error);
  }

  return response.json() as Promise<UserKycStatusResponse>;
}

// Valida las reglas de negocio antes de iniciar un nuevo proceso KYC.
// HTTP 201 → el usuario puede iniciar.
// HTTP 409 → usuario bloqueado (lanza KYCApiError con details del motivo).
export async function startKycProcess(userId: string, token: string): Promise<void> {
  const response = await fetch(`${WORKFLOWS_URL}/api/v1/kyc/start`, {
    method: 'POST',
    headers: {
      ...buildAuthHeaders(token),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ userId }),
  });

  if (!response.ok) {
    const error = await parseErrorResponse(response);
    throw new KYCApiError(error);
  }
}

// Reinicia el proceso KYC de un usuario en estado 'completed'.
// Solo disponible cuando ALLOW_KYC_RESET=true (sandbox / debug).
// HTTP 403 → reset no habilitado. HTTP 409 → usuario no está en 'completed'.
export async function resetKycProcess(userId: string, token: string): Promise<void> {
  const response = await fetch(
    `${WORKFLOWS_URL}/api/v1/kyc/reset/${encodeURIComponent(userId)}`,
    {
      method: 'POST',
      headers: buildAuthHeaders(token),
    }
  );

  if (!response.ok) {
    const error = await parseErrorResponse(response);
    throw new KYCApiError(error);
  }
}

export type {
  UploadDocumentRequest,
  UploadDocumentResponse,
  ScoreDocumentRequest,
  ScoreDocumentResponse,
  ExecuteWorkflowRequest,
  ExecuteWorkflowResponse,
  CreateLivenessSessionResponse,
  LivenessResultResponse,
  LivenessCredentialsResponse,
  StatsResponse,
  UserSummary,
  ApiError,
  MergeIneRequest,
  MergeIneResponse,
  KycLastResult,
  UserKycStatusResponse,
};
